import { useReducer, useMemo } from 'react'
import { createStack, push, pop } from 'utils/stacks'

// Navigation reducer for centralized state management
const navigationReducer = (state, action) => {
  switch (action.type) {
    case 'PUSH_ROUTE':
      return {
        ...state,
        routeStack: push(state.routeStack, action.payload.route),
        baseOffset: state.baseOffset + 1,
        navigationInProgress: true,
      }
    case 'POP_ROUTE': {
      const [, newRouteStack] = pop(state.routeStack)
      return {
        ...state,
        routeStack: newRouteStack,
        baseOffset: state.baseOffset - 1,
        navigationInProgress: false,
      }
    }

    case 'SET_DEVICE_ID':
      return {
        ...state,
        deviceId: action.payload.deviceId,
      }

    case 'SET_NAVIGATION_PROGRESS':
      return {
        ...state,
        navigationInProgress: action.payload.inProgress,
      }

    case 'SET_LOADING_SCREEN':
      return {
        ...state,
        loadingScreen: action.payload.loading,
      }

    case 'ADD_PENDING_NAVIGATION':
      return {
        ...state,
        pendingNavigations: [
          ...state.pendingNavigations,
          action.payload.navigation,
        ],
      }

    case 'CLEAR_PENDING_NAVIGATIONS':
      return {
        ...state,
        pendingNavigations: [],
      }

    case 'RESET_STATE':
      return {
        ...state,
        routeStack: action.payload.routeStack || state.routeStack,
        baseOffset:
          action.payload.baseOffset !== undefined
            ? action.payload.baseOffset
            : -1,
        navigationInProgress: false,
        loadingScreen: false,
        pendingNavigations: [],
      }

    default:
      console.warn(
        '🚨 useNavigationState: Unknown reducer action:',
        action.type
      )
      return state
  }
}

export const useNavigationState = (initialComponentId, initialDeviceId) => {
  // Memoize initial route stack
  const initialRouteStack = useMemo(
    () =>
      createStack({
        target: initialComponentId,
        params: {},
        transition: null,
      }),
    [initialComponentId]
  )

  // Initialize navigation state with reducer
  const [navigationState, dispatch] = useReducer(navigationReducer, {
    routeStack: initialRouteStack,
    deviceId: initialDeviceId,
    baseOffset: -1,
    navigationInProgress: false,
    loadingScreen: false,
    pendingNavigations: [],
  })

  // Memoize state values to prevent unnecessary re-renders
  const memoizedState = useMemo(() => navigationState, [navigationState])

  return {
    ...memoizedState,
    dispatch,
  }
}
